import { useEffect } from "react";

interface SizeMessage {
  type: "lumicle:resize";
  source: "lumicle-stats";
  height: number;
  width: number;
}

/**
 * When `enabled`, observes the document body's bounding box and posts the
 * current height to the embedding parent window. Embedders can listen for
 * `message` events of type `lumicle:resize` to set the iframe's height
 * dynamically.
 */
export function useAutosize(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    if (window.parent === window) return; // not embedded

    const send = () => {
      const root = document.documentElement;
      const height = Math.max(
        document.body?.scrollHeight ?? 0,
        root.scrollHeight,
        root.offsetHeight
      );
      const width = Math.max(
        document.body?.scrollWidth ?? 0,
        root.scrollWidth,
        root.offsetWidth
      );
      const msg: SizeMessage = {
        type: "lumicle:resize",
        source: "lumicle-stats",
        height,
        width,
      };
      try {
        window.parent.postMessage(msg, "*");
      } catch {
        /* noop */
      }
    };

    send();

    const ro = new ResizeObserver(() => send());
    if (document.body) ro.observe(document.body);

    const onResize = () => send();
    window.addEventListener("resize", onResize);

    // Re-send a few times during initial render so async data arrival is captured
    const t1 = setTimeout(send, 200);
    const t2 = setTimeout(send, 800);
    const t3 = setTimeout(send, 1800);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [enabled]);
}
